import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useResizablePanelWidth — drag-to-resize + width persistence for a
 * right-anchored overlay panel, or a left-anchored column (see `anchor`).
 *
 * Extracted from the inline implementation in `BuildingDataSidebar`
 * (WHA-3644) so the PM issue detail panel can resize the same way without
 * duplicating the pointer math or adding MST/root-store boilerplate.
 *
 * Two rules keep the stored width honest, and both are load-bearing:
 *
 * - **Only a deliberate resize is persisted.** Until the user drags the handle
 *   or nudges it with the keyboard there is no stored preference at all, so a
 *   caller passing a viewport-dependent `defaultWidth` keeps its responsive
 *   behaviour instead of having whatever the first mount happened to measure
 *   pinned into `localStorage` forever.
 * - **The viewport clamps rendering, never the preference.** A window narrow
 *   enough to squeeze the panel borrows the width for as long as it is narrow;
 *   widening it again hands the user's chosen width straight back. A panel that
 *   is bounded by its container rather than the window (an in-flow flex child,
 *   not an overlay) passes `maxWidth` and gets the same treatment.
 *
 * The returned `handleProps` are spread onto the left-edge resize handle of a
 * right-anchored panel (a `role="separator"` element): dragging the handle
 * leftward grows the panel, as do ArrowLeft / Home for keyboard users. They
 * carry the separator's `aria-value*` attributes, so the handle reports the
 * width it is actually rendering.
 */

const DRAG_THRESHOLD_PX = 4;
const MAX_WIDTH_RATIO = 0.65;
const MAX_WIDTH_FALLBACK = 720;
/** Arrow-key step. Shift multiplies it, mirroring a native range input. */
const KEYBOARD_STEP_PX = 16;
const KEYBOARD_STEP_MULTIPLIER = 5;

/** Current maximum width as 65 % of the viewport (SSR-safe). */
function getMaxWidth(): number {
  if (typeof window === "undefined") return MAX_WIDTH_FALLBACK;
  return Math.round(window.innerWidth * MAX_WIDTH_RATIO);
}

/**
 * The stored preference, or `null` when the user has never resized this panel.
 * Clamped to `min` (a code constant) but deliberately NOT to the viewport —
 * that clamp belongs to rendering.
 */
function loadStoredWidth(storageKey: string, min: number): number | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    // Must be a JSON number, not merely something `Number()` can coerce:
    // `null`, `true` and `[]` all coerce to a finite value, which would clamp
    // to `min` AND register as a deliberate preference — pinning a corrupted
    // key to the narrowest panel and disabling the responsive default.
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "number" || !Number.isFinite(parsed)) return null;
    return Math.max(min, Math.round(parsed));
  } catch {
    return null;
  }
}

interface UseResizablePanelWidthOptions {
  /** Lower bound on the panel width. Defaults to 360px. */
  minWidth?: number;
  /**
   * Upper bound for a panel whose room is decided by its container rather than
   * the window: an in-flow flex child has to leave its siblings their space,
   * and 65 % of the viewport can be more than the row has left. Applied ON TOP
   * of the viewport ceiling, never instead of it, and `undefined` until the
   * caller has measured — which leaves the viewport ceiling in charge.
   */
  maxWidth?: number;
  /**
   * Which edge the panel is pinned to, and therefore which way the handle
   * grows it.
   *
   * `right` (the default) is a right-anchored overlay whose handle is on its
   * LEFT edge, so dragging left grows it. `left` is the mirror — a column
   * pinned to the left of a grid with its handle on its RIGHT edge, where
   * dragging right grows it. Without this the Gantt's label column would
   * shrink as you pulled it wider.
   */
  anchor?: "left" | "right";
}

export function useResizablePanelWidth(
  storageKey: string,
  defaultWidth = 420,
  options: UseResizablePanelWidthOptions = {}
) {
  const min = options.minWidth ?? 360;
  const anchor = options.anchor ?? "right";
  const [preferredWidth, setPreferredWidth] = useState<number | null>(() =>
    loadStoredWidth(storageKey, min)
  );
  const [maxWidth, setMaxWidth] = useState(getMaxWidth);
  // The width a drag started from — `null` between drags, so it doubles as
  // "is a drag in progress" and as the settled width below.
  const [dragStartWidth, setDragStartWidth] = useState<number | null>(null);
  const dragging = dragStartWidth !== null;
  const [hovered, setHovered] = useState(false);
  // Focus is tracked separately from hover: sharing one flag let a pointer
  // leaving the handle clear the accent line while it still had keyboard
  // focus, so Arrow keys went on resizing an element with no focus indicator.
  const [focused, setFocused] = useState(false);
  const dragStartRef = useRef<{ startX: number; startWidth: number; dragged: boolean } | null>(
    null
  );

  // In a viewport (or container) narrow enough that the ceiling falls below
  // `min`, it would sit under the floor: the separator would report an
  // `aria-valuenow` above its `aria-valuemax`, and End would land below Home.
  // `min` wins.
  const containerMax = options.maxWidth;
  const effectiveMax = Math.max(
    min,
    containerMax === undefined ? maxWidth : Math.min(maxWidth, Math.round(containerMax))
  );

  // `defaultWidth` is a plain argument, so a caller computing it from the
  // viewport re-supplies it on the re-render the resize listener triggers —
  // which is how an unresized panel stays responsive.
  const width = Math.max(min, Math.min(effectiveMax, Math.round(preferredWidth ?? defaultWidth)));

  // The shared write path for both input paths, and where the second rule is
  // enforced: the viewport clamps rendering, never the preference (see the
  // header). A request that cannot move the rendered width would otherwise
  // store the borrowed width — resizing in a narrow window, by key or by drag,
  // would quietly replace a wider stored preference with the clamp, and
  // widening the window would never hand it back. Both ends need the guard: in
  // a viewport too narrow for `min` the ceiling collapses onto the floor, so a
  // shrink at the floor is exactly as much of a no-op as a grow at the ceiling.
  const setWidth = useCallback(
    (value: number) => {
      if (width >= effectiveMax && value >= width) return;
      if (width <= min && value <= width) return;
      setPreferredWidth(Math.max(min, Math.min(effectiveMax, Math.round(value))));
    },
    [min, width, effectiveMax]
  );

  /** The debounced write, also called directly to flush it. */
  const pendingWriteRef = useRef<{ storageKey: string; width: number } | null>(null);
  const flushStoredWidth = useCallback(() => {
    const pending = pendingWriteRef.current;
    if (!pending) return;
    pendingWriteRef.current = null;
    try {
      window.localStorage.setItem(pending.storageKey, JSON.stringify(pending.width));
    } catch {
      // Quota / privacy-mode failures are non-fatal for a UI preference.
    }
  }, []);

  // Mirror the chosen width into localStorage. A 200ms trailing debounce keeps
  // a resize drag (one update per pointermove pixel) off the main thread.
  // `null` means the user has never resized: nothing to remember.
  useEffect(() => {
    if (preferredWidth === null) return;
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
    pendingWriteRef.current = { storageKey, width: preferredWidth };
    const handle = setTimeout(flushStoredWidth, 200);
    return () => clearTimeout(handle);
  }, [storageKey, preferredWidth, flushStoredWidth]);

  // The debounce is cleared on unmount, so a panel closed within 200ms of the
  // drag ending (the inspector's X, the issue panel's Escape) would lose the
  // resize entirely. Flush whatever the timer had not written yet.
  useEffect(() => flushStoredWidth, [flushStoredWidth]);

  // Track the viewport ceiling so the handle and panel stay reachable in a
  // narrow window. This only moves what we render — see the header.
  useEffect(() => {
    const onResize = () => setMaxWidth(getMaxWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only the primary button drags. A right-click arms the drag but its
      // context menu can swallow the matching `pointerup`, leaving the handle
      // resizing on a bare `pointermove` with no button held.
      if (e.button !== 0) return;
      e.preventDefault();
      const handle = e.currentTarget as HTMLDivElement;
      // `preventDefault` suppresses the compatibility `mousedown` and with it
      // the default focus action, so a click would leave focus wherever it was
      // and send the Arrow / Home / End keys below to the panel body instead of
      // the separator. Focus it by hand rather than dropping the
      // text-selection suppression the drag needs.
      handle.focus();
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        // `NotFoundError` when the pointer is no longer active — the press is
        // still a real press, so arm the drag anyway. Without capture nothing
        // is delivered here once the pointer leaves the handle: the release
        // lands elsewhere and `lostpointercapture` never fires, so the
        // button-less check in `onPointerMove` is what ends this drag.
      }
      dragStartRef.current = { startX: e.clientX, startWidth: width, dragged: false };
      setDragStartWidth(width);
    },
    [width]
  );

  const endDrag = useCallback(() => {
    dragStartRef.current = null;
    setDragStartWidth(null);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current;
      if (!start) return;
      // No button held means the press already ended somewhere we never heard
      // about — the release lands off the handle whenever the capture was
      // refused, and `lostpointercapture` cannot fire without a capture. Left
      // armed, the drag would freeze `settledWidth` for the panel's life and
      // resize on the next bare hover over the handle.
      if (e.buttons === 0) {
        endDrag();
        return;
      }
      // Right-anchored: the handle is on the left edge, so leftward (a
      // negative clientX delta) grows it. Left-anchored is the mirror.
      const dx = anchor === "left" ? e.clientX - start.startX : start.startX - e.clientX;
      if (!start.dragged && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      start.dragged = true;
      // Dragging the handle away from the panel's anchor grows it.
      // (width goes up as clientX decreases).
      setWidth(start.startWidth + dx);
    },
    [setWidth, endDrag]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      endDrag();
      try {
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
      } catch {
        // Pointer may already be released on pointer cancel.
      }
    },
    [endDrag]
  );

  // The UA can take the capture away without ever delivering `pointerup` (a
  // context menu, a device disconnecting). Both halves of the drag state have
  // to come down with it: a live `dragStartRef` resizes on the next bare
  // `pointermove`, and a non-null `dragStartWidth` freezes `settledWidth` for
  // the panel's life, so the chat pill stops tracking the panel edge.
  // `lostpointercapture` also fires on a normal release, where this is a no-op.
  const onLostPointerCapture = endDrag;

  // Keyboard parity for the separator: without it the panel is pointer-only.
  // Left grows and right shrinks, matching which way the drag has to go.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? KEYBOARD_STEP_PX * KEYBOARD_STEP_MULTIPLIER : KEYBOARD_STEP_PX;
      let next: number;
      switch (e.key) {
        // The arrow that points AWAY from the anchor grows the panel, so the
        // key matches the direction the edge visibly travels.
        case "ArrowLeft":
          next = anchor === "left" ? width - step : width + step;
          break;
        case "ArrowRight":
          next = anchor === "left" ? width + step : width - step;
          break;
        case "Home":
          next = min;
          break;
        case "End":
          next = effectiveMax;
          break;
        default:
          return;
      }
      e.preventDefault();
      // A key that cannot widen the panel any further is dropped by `setWidth`.
      setWidth(next);
    },
    [min, width, effectiveMax, setWidth]
  );

  const handleProps = useMemo(
    () => ({
      tabIndex: 0,
      "aria-valuenow": width,
      "aria-valuemin": min,
      "aria-valuemax": effectiveMax,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onLostPointerCapture,
      onKeyDown,
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      // Keyboard focus reuses the hover accent so the handle is visible while
      // it is being nudged — every caller already renders on `hover`.
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    }),
    [
      width,
      min,
      effectiveMax,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onLostPointerCapture,
      onKeyDown,
    ]
  );

  // The width as it stands between drags. Anything that reacts to the panel's
  // size by touching the page rather than the panel — `useBottomRightExclusion`
  // writes a custom property on `document.documentElement` and wakes the chat
  // widget — should follow this instead of `width`, so a drag doesn't
  // invalidate layout globally on every pointermove.
  const settledWidth = dragStartWidth ?? width;

  // `hover` is the affordance flag rather than the pointer state: the handle
  // shows its accent line whenever it is hovered OR focused.
  return { width, settledWidth, dragging, hover: hovered || focused, handleProps };
}
